import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * Lets another page send someone to the map with one person already picked.
 *
 * <p>The profile's recommendations link here with {@code state: { focusId }}. Once the map's own
 * search has loaded and that person is among the results, {@code onFocus} is called once with their
 * id - the map then selects them and opens their card, which is where Connect lives, so the
 * connection still goes through the map's consent and safety checks. If they are not in the current
 * results (a filter left them out) nothing happens, and the map is just the map.
 */
export function useFocusedFromNavigation(
  items: { id: string }[] | undefined,
  onFocus: (id: string) => void,
) {
  const location = useLocation()
  const navigate = useNavigate()
  const focusId = (location.state as { focusId?: string } | null)?.focusId
  const handled = useRef<string | null>(null)

  useEffect(() => {
    if (!focusId || !items || handled.current === focusId) return
    if (items.some((item) => item.id === focusId)) {
      handled.current = focusId
      onFocus(focusId)
      // Used once: history state outlives a reload and Back, and would open this card again each time.
      navigate(location.pathname + location.search, { replace: true, state: null })
    }
    // onFocus only sets state; leaving it out keeps this from re-running on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, items])
}
